import { IconName, byPrefixAndName } from '@awesome.me/kit-5c288c4b3f/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Popover,
  PopoverButton,
  PopoverPanel,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
  Transition,
} from '@headlessui/react'
import { useMatch } from '@reach/router'
import { graphql, useStaticQuery } from 'gatsby'
import React, { useState } from 'react'
import { classNames } from '../utils'
import { Alert } from './Alert'
import { Link } from '../components/link'

interface BaseNavProps {
  name: string
  id?: string
  slug?: string
  url?: string
}
interface NavProps extends BaseNavProps {
  level1?: Level1NavProps[]
}

interface Level1NavProps extends BaseNavProps {
  subNav2?: Level2NavProps[]
}

interface Level2NavProps extends BaseNavProps {
  description?: string
  icon?: {
    iconName: IconName
  }
  subNav3?: Level3NavProps[]
}

interface Level3NavProps extends BaseNavProps {
  subNav4?: Level4NavProps[]
}

interface Level4NavProps extends BaseNavProps {
  description?: string
  icon?: {
    iconName: IconName
  }
}

const NavBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const data = useStaticQuery(graphql`
    query NavBarQuery {
      dv01LogoWhite: contentfulAsset(
        contentful_id: { eq: "1A6cMInS2mBWi5IMerlNoF" }
      ) {
        id
        title
        file {
          url
        }
      }
      dv01LogoBlack: contentfulAsset(
        contentful_id: { eq: "7asjsSja05dwKEVnWTN0OM" }
      ) {
        id
        title
        file {
          url
        }
      }
      contentfulAlert(contentful_id: { eq: "2OJ4KQFVkhhqZij5HjTGGQ" }) {
        id
        text {
          text
        }
        cta
        ctaExternalLink
        show
      }
      fitchNav: contentfulMainNav(
        contentful_id: { eq: "281In4gLSnxW3n7dvDubbV" }
      ) {
        name
        id
        level1 {
          name
          url
        }
      }
      mainNav: contentfulMainNav(
        contentful_id: { eq: "3xz77y5gqhxbTAqxljUi3j" }
      ) {
        name
        id
        level1 {
          id
          name
          url
          slug
          subNav2 {
            id
            name
            description
            slug
            subNav3 {
              id
              name
              slug
              subNav4 {
                id
                name
                description
                slug
                url
                icon {
                  iconName
                }
              }
            }
          }
        }
      }
      mobileNav: contentfulMainNav(
        contentful_id: { eq: "15gNqF1FvRnOYp5gYPrrKe" }
      ) {
        id
        name
        level1 {
          name
          id
          slug
          subNav2 {
            name
            id
            slug
            icon {
              iconName
            }
          }
        }
      }
    }
  `)

  return (
    <header className="fixed z-30 w-full">
      {/* FITCH NAV */}
      <div className="relative z-10 bg-gray-1000">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-start py-1 md:space-x-10">
            <nav
              className="hidden items-center space-x-10 text-gray-50 md:flex md:flex-1 lg:w-0"
              aria-label={`${data.fitchNav.name} menu`}
            >
              <div className="relative">
                <Menu>
                  {({ open }) => (
                    <>
                      <MenuButton>
                        <span className="text-md">
                          {data.fitchNav.name}{' '}
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fas['caret-down']}
                            className="h-4 w-auto"
                          />
                        </span>
                      </MenuButton>
                      <Transition
                        show={open}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <div className="absolute z-10 -ml-4 mt-1 w-auto transform px-2 sm:px-0 lg:left-0 lg:ml-0">
                          <div className="overflow-hidden rounded-md shadow-lg">
                            <MenuItems
                              static
                              modal={false}
                              unmount={false}
                              className="relative grid bg-gray-50"
                            >
                              {data.fitchNav.level1.map(
                                (item: Level1NavProps) => (
                                  <MenuItem key={item.id}>
                                    {({ focus }) => (
                                      <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        className={`${
                                          focus ? 'bg-[#174174]' : 'bg-gray-50'
                                        } group flex items-center px-5 py-3 transition-all`}
                                      >
                                        <p
                                          className={`${
                                            focus
                                              ? 'text-gray-50'
                                              : 'text-gray-900'
                                          } whitespace-nowrap text-md transition-all`}
                                        >
                                          {item.name}
                                        </p>
                                      </a>
                                    )}
                                  </MenuItem>
                                )
                              )}
                            </MenuItems>
                          </div>
                        </div>
                      </Transition>
                    </>
                  )}
                </Menu>
              </div>
            </nav>
          </div>
        </div>
      </div>
      {/* END FITCH NAV */}
      {/* MAIN NAV */}
      <div className="relative bg-gray-1000">
        <div className="container relative mx-auto px-4">
          <div className="flex items-center justify-between py-4 md:justify-start md:space-x-10">
            <div className="flex justify-start lg:w-0 lg:flex-1">
              {/* LOGO HOME */}
              <Link to="/">
                <span className="sr-only">dv01</span>
                <object
                  className="pointer-events-none inline-flex h-10 md:h-14"
                  type="image/svg+xml"
                  title={data.dv01LogoWhite.title}
                  data={data.dv01LogoWhite.file.url}
                >
                  <span className="sr-only">dv01 Logo</span>
                  {data.dv01LogoWhite.title}
                </object>
              </Link>
            </div>
            {/* MOBILE OPEN MENU */}
            <div className="-my-2 -mr-2 md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                type="button"
                className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-primary-600"
                aria-expanded="false"
              >
                <span className="sr-only">Open menu</span>
                <FontAwesomeIcon icon={byPrefixAndName.fal['bars']} />
              </button>
            </div>
            {/* DESKTOP NAV */}
            <nav
              className="hidden items-center justify-end space-x-10 text-gray-50 md:flex md:flex-1 lg:w-0"
              aria-label={`${data.mainNav.name} menu`}
            >
              {data.mainNav.level1.map((nav: Level1NavProps) => {
                const match = useMatch(`/${nav.slug}/*`)
                if (nav.subNav2 === null) {
                  return (
                    <Link
                      to={`/${nav.slug}`}
                      key={nav.id}
                      activeClassName="text-primary"
                      partiallyActive
                      className="flex h-6 items-center text-sm font-medium hover:text-primary"
                    >
                      {nav.name}
                    </Link>
                  )
                } else if (nav.slug !== 'offerings') {
                  return (
                    <div className="relative" key={nav.id}>
                      <Menu>
                        <>
                          <MenuButton
                            className={classNames(
                              match?.uri === `/${nav.slug}`
                                ? 'text-primary'
                                : 'text-gray-50 hover:text-primary',
                              'group inline-flex h-6 items-center rounded-md text-sm font-medium focus:outline-2 focus:outline-offset-2 focus:outline-primary-600'
                            )}
                          >
                            <span>{nav.name}</span>
                          </MenuButton>

                          <Transition
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                          >
                            <div className="absolute z-10 -ml-4 mt-3 w-auto transform px-2 sm:px-0 lg:left-1/2 lg:ml-0 lg:-translate-x-1/2">
                              <div className="box-arrow overflow-hidden rounded-md shadow-lg">
                                <MenuItems
                                  static
                                  modal={false}
                                  className="relative grid gap-xl bg-gray-50 p-l"
                                >
                                  {nav.subNav2.map(
                                    ({ name, slug, id }: Level2NavProps) => (
                                      <MenuItem key={id}>
                                        {({ focus }) => (
                                          <Link
                                            key={`${nav.slug}-${slug}`}
                                            to={`/${nav.slug}/${slug}`}
                                            activeClassName="active text-primary-dark"
                                            className={`${
                                              focus ? 'bg-gray-100' : ''
                                            } group -m-3 flex items-center rounded-lg p-s hover:bg-gray-100`}
                                          >
                                            <p className="active-nav-text whitespace-nowrap text-sm font-medium text-gray-900">
                                              {name}
                                            </p>
                                          </Link>
                                        )}
                                      </MenuItem>
                                    )
                                  )}
                                </MenuItems>
                              </div>
                            </div>
                          </Transition>
                        </>
                      </Menu>
                    </div>
                  )
                } else {
                  return (
                    <div className="" key={nav.id}>
                      <Popover className="isolate z-50">
                        <PopoverButton
                          className={classNames(
                            match?.uri === `/${nav.slug}`
                              ? 'text-primary'
                              : 'text-gray-50 hover:text-primary',
                            'group inline-flex items-center rounded-md text-sm font-medium focus:outline-2 focus:outline-offset-2 focus:outline-primary-600'
                          )}
                        >
                          <span>{nav.name}</span>
                        </PopoverButton>

                        <Transition
                          enter="transition ease-out duration-200"
                          enterFrom="opacity-0 translate-y-1"
                          enterTo="opacity-100 translate-y-0"
                          leave="transition ease-in duration-150"
                          leaveFrom="opacity-100 translate-y-0"
                          leaveTo="opacity-0 translate-y-1"
                        >
                          <PopoverPanel className="absolute inset-x-0 top-24 -z-10 rounded-2xl border-8 border-gray-850 bg-gray-950 shadow-lg">
                            <TabGroup className="grid grid-cols-12">
                              <TabList className="col-span-2 flex flex-col items-start gap-5 bg-gray-950">
                                {nav.subNav2.map(
                                  ({ name, id, description }) => (
                                    <Tab
                                      key={id}
                                      className="p-l data-[selected]:bg-gray-850"
                                    >
                                      <h2 className="text-left text-md text-gray-50">
                                        {name}
                                      </h2>
                                      <p className="text-left text-sm text-gray-400">
                                        {description}
                                      </p>
                                    </Tab>
                                  )
                                )}
                              </TabList>
                              <TabPanels className="col-span-10 bg-gray-850 p-xl">
                                {nav.subNav2.map(
                                  ({ id, subNav3 }: Level2NavProps) => {
                                    // to calculate corrent number of columns coming from CMS
                                    let totalCol: number
                                    if (12 % subNav3.length === 0) {
                                      totalCol = 12
                                    } else if (10 % subNav3.length === 0) {
                                      totalCol = 10
                                    }
                                    return (
                                      <TabPanel key={id}>
                                        <div
                                          className={`grid grid-cols-${totalCol} gap-l`}
                                        >
                                          {subNav3 &&
                                            subNav3?.map(
                                              ({
                                                name,
                                                id,
                                                subNav4,
                                              }: Level3NavProps) => (
                                                <div
                                                  key={id}
                                                  className={`col-span-${totalCol / subNav3.length}`}
                                                >
                                                  <h3 className="mb-l pl-m text-md font-medium text-gray-50">
                                                    {name}
                                                  </h3>
                                                  {subNav4.map(
                                                    ({
                                                      name,
                                                      description,
                                                      id,
                                                      icon,
                                                      slug,
                                                      url,
                                                    }: Level4NavProps) => (
                                                      <Link
                                                        key={id}
                                                        to={
                                                          slug
                                                            ? `/${nav.slug}/${slug}`
                                                            : url
                                                        }
                                                        activeClassName="active text-primary-dark"
                                                        className="group flex items-center gap-s rounded-lg py-m text-gray-50 hover:bg-gray-850"
                                                        target={
                                                          slug
                                                            ? '_self'
                                                            : '_blank'
                                                        }
                                                      >
                                                        {icon && (
                                                          <div className="active-nav-icon flex h-8 w-8 items-center justify-center rounded-full bg-gray-750 text-gray-400 group-hover:bg-primary group-hover:text-gray-50">
                                                            {icon.iconName ===
                                                            'recycle' ? (
                                                              <span className="fa-layers fa-fw">
                                                                <FontAwesomeIcon
                                                                  icon={
                                                                    byPrefixAndName
                                                                      .fal[
                                                                      icon
                                                                        .iconName
                                                                    ]
                                                                  }
                                                                  transform="grow-4"
                                                                />
                                                                <FontAwesomeIcon
                                                                  icon={
                                                                    byPrefixAndName
                                                                      .fal[
                                                                      'signal-good'
                                                                    ]
                                                                  }
                                                                  transform="shrink-7 right-2 up-2"
                                                                />
                                                              </span>
                                                            ) : (
                                                              <FontAwesomeIcon
                                                                icon={
                                                                  byPrefixAndName
                                                                    .fal[
                                                                    icon
                                                                      .iconName
                                                                  ]
                                                                }
                                                              />
                                                            )}
                                                          </div>
                                                        )}
                                                        <div className="flex flex-col">
                                                          <p className="active-nav-text text-xxs font-bold">
                                                            {name}
                                                          </p>
                                                          <p className="text-xxs text-gray-400 group-hover:text-gray-50">
                                                            {description}
                                                          </p>
                                                        </div>
                                                      </Link>
                                                    )
                                                  )}
                                                </div>
                                              )
                                            )}
                                        </div>
                                      </TabPanel>
                                    )
                                  }
                                )}
                              </TabPanels>
                            </TabGroup>
                          </PopoverPanel>
                        </Transition>
                      </Popover>
                    </div>
                  )
                }
              })}
              <a
                href="https://dv01app.com/"
                target="_blank"
                rel="noreferrer noopener"
                className="whitespace-nowrap rounded bg-primary-base px-m py-s text-sm font-medium text-gray-1000 hover:bg-primary-600 hover:text-gray-50"
              >
                Log In
              </a>
            </nav>
          </div>
        </div>
      </div>
      {/* END MAIN NAV */}
      {/* Mobile Menu */}
      <Transition
        show={mobileMenuOpen}
        enter="duration-200 ease-out"
        enterFrom="opacity-0 scale-100"
        enterTo="opacity-100 scale-100"
        leave="duration-100 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-100"
      >
        <div className="absolute inset-x-0 bottom-0 top-0 z-40 h-svh transform overflow-auto transition md:hidden">
          <nav
            className="divide-y-2 divide-gray-50 bg-gray-50"
            aria-label={`${data.mobileNav.name} menu`}
          >
            <div className="flex items-center justify-between p-xl pt-5">
              <div>
                {/* MOBILE LOGO */}
                <object
                  className="pointer-events-none inline-flex h-12"
                  type="image/svg+xml"
                  title={data.dv01LogoBlack.title}
                  data={data.dv01LogoBlack.file.url}
                >
                  {data.dv01LogoBlack.title}
                </object>
              </div>
              {/* MOBILE CLOSE MENU */}
              <div className="">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  type="button"
                  className="inline-flex items-center justify-center rounded-md bg-gray-50 p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-primary-600"
                >
                  <span className="sr-only">Close menu</span>
                  <FontAwesomeIcon
                    size="2x"
                    icon={byPrefixAndName.fal['xmark']}
                  />
                </button>
              </div>
            </div>

            {data.mobileNav.level1.map((nav: Level1NavProps) => {
              if (nav.subNav2 === null) {
                return (
                  <div className="p-xl" key={nav.id}>
                    <div className="flex gap-y-4">
                      <Link
                        key={`mobile-${nav.id}`}
                        to={`/${nav.slug}`}
                        activeClassName="active"
                        className="group -m-3 flex items-center rounded-md p-3 hover:bg-gray-50"
                      >
                        <span className="active-nav-text ml-2 text-base font-medium text-gray-900">
                          {nav.name}
                        </span>
                      </Link>
                    </div>
                  </div>
                )
              } else {
                return (
                  <div className="p-xl" key={nav.id}>
                    <div className="flex flex-col">
                      <div key={`mobile-${nav.id}`}>
                        <span className="active-nav-text mb-2 ml-2 text-xl font-bold text-gray-900">
                          {nav.name}
                        </span>
                        {nav.subNav2.map((level2: Level2NavProps) => (
                          <Link
                            key={`mobile-${level2.id}`}
                            to={`/${nav.slug}/${level2.slug}`}
                            activeClassName="active text-primary-dark"
                            className="group ml-2 flex items-center rounded-md p-2 hover:bg-gray-50"
                          >
                            {level2.icon && (
                              <div className="active-nav-icon ml-2 flex h-7 w-7 items-center justify-center rounded-full bg-primary-lighter text-primary">
                                <FontAwesomeIcon
                                  size="sm"
                                  icon={
                                    byPrefixAndName.fal[level2?.icon?.iconName]
                                  }
                                />
                              </div>
                            )}
                            <span className="active-nav-text ml-3 text-base font-medium text-gray-900">
                              {level2.name}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              }
            })}

            <div className="p-xl">
              <p className="text-center text-base font-medium text-gray-500">
                Existing customer?{' '}
                <a
                  href="https://dv01app.com/"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-indigo-600 hover:text-indigo-500"
                >
                  Log in
                </a>
              </p>
            </div>
          </nav>
        </div>
      </Transition>

      {data.contentfulAlert.show && (
        <Alert
          cta={data.contentfulAlert.cta}
          ctaExternalLink={data.contentfulAlert.ctaExternalLink}
        >
          {data.contentfulAlert.text.text}
        </Alert>
      )}
    </header>
  )
}

export default NavBar
